import { Stack } from '@mui/material';
import { logger } from '../../services/logger/logger';
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import Chips from '../../components/common/Chips';
import ClientSideAgGrid from '../../components/common/customAgGrid/ClientSideAgGrid';
import CustomActionButton from '../../components/common/customAgGrid/CustomActionButton';
import CustomAgMultiSelectFilter from '../../components/common/customAgGrid/CustomAgMultiSelectFilter';
import CustomAgTextFilter from '../../components/common/customAgGrid/CustomAgTextFilter';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import EditLubricantCategoryModal from '../../components/lubricants/EditLubricantCategoryModal';
import PageHeader from '../../components/PageHeader';
import {
  useCreateLubricantCategoryMutation,
  useDeleteLubricantCategoryMutation,
  useGetLubricantCategoriesListQuery,
  useUpdateLubricantCategoryMutation,
} from '../../store/apiSlices/lubricants/lubricantCategory/lubricantCategoryApis';
import { canEditReferenceData } from '../../utils/permissions';
import { showSuccessToast } from '../../components/common/BaseToaster';
import { TableCardContent } from '../../components/common/styled/TableCardContent';
import { useDeleteConfirmation } from '../../hooks/useDeleteConfirmation';
import { useTranslation } from 'react-i18next';

// ------------------- Types -------------------
interface LubricantCategory {
  id: number;
  categoryName: string;
  primary: string | boolean; // Can be "Active"/"Inactive" from API or boolean
  sortOrder: number;
  sumpTank: string | boolean; // Can be "Active"/"Inactive" from API or boolean
}

interface ActionButton {
  label: string;
  variant: 'text' | 'blue' | 'outlinedBlue' | 'red' | 'gray' | undefined;
  onClick: () => void;
}

interface BreadcrumbOption {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

// ------------------- Component -------------------
type CategoryFormValues = {
  categoryName: string;
  primary: boolean;
  sortOrder: string;
  sumpTank: boolean;
};

const LubricantCategoriesContainer: React.FC = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const canAddorEdit = canEditReferenceData(userInfo?.authorityName);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState<boolean>(false);
  const [editingCategory, setEditingCategory] = useState<LubricantCategory | null>(null);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [resetFiltersSignal, setResetFiltersSignal] = useState(false);
  const [tableColumns, setTableColumns] = useState<ColDef[]>([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    defaultValues: {
      categoryName: '',
      primary: false,
      sortOrder: '',
      sumpTank: false,
    },
  });

  // API calls
  const {
    data: lubricantCategoriesResponse,
    isLoading,
    refetch,
  } = useGetLubricantCategoriesListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // Create / Update / Delete category mutations
  const [createLubricantCategory, { isLoading: isCreating }] = useCreateLubricantCategoryMutation();
  const [updateLubricantCategory, { isLoading: isUpdating }] = useUpdateLubricantCategoryMutation();
  const [deleteLubricantCategory, { isLoading: isDeleting }] = useDeleteLubricantCategoryMutation();

  const { showDeleteConfirm, handleConfirmDelete, onDeleteClick, onCloseDeleteConfirm } =
    useDeleteConfirmation<LubricantCategory>({
      deleteMutation: deleteLubricantCategory,
      refetch,
      itemName: 'category',
      idField: 'id',
    });

  // Extract data from response
  const lubricantCategoriesList = lubricantCategoriesResponse?.data || [];

  // Ensure API refetches when needed
  useEffect(() => {
    refetch();
  }, [refetch]);

  const handleAddCategory = () => {
    const higherShowOrder = lubricantCategoriesList.reduce(
      (max, category) => Math.max(max, category.sortOrder),
      0,
    );
    reset({
      categoryName: '',
      primary: false,
      sortOrder: (higherShowOrder + 1).toString(),
      sumpTank: false,
    });
    setShowAddCategoryModal(true);
  };

  // Page header action buttons
  const actionBtns: ActionButton[] = canAddorEdit
    ? [
        {
          label: t('lubricants.addCategory'),
          variant: 'blue',
          onClick: handleAddCategory,
        },
      ]
    : [];

  // Breadcrumb options
  const breadcrumbOptions: BreadcrumbOption[] = [
    { label: t('lubricants.categories'), isActive: true, onClick: () => {} },
  ];

  // Handle edit button click
  const handleEditClick = useCallback(
    (rowData: LubricantCategory) => {
      setEditingCategory(rowData);

      // Convert string values ("Active"/"Inactive") back to boolean
      // The API parser converts booleans to strings for display in the table
      const primaryValue = rowData.primary === 'Active' || rowData.primary === true;
      const sumpTankValue = rowData.sumpTank === 'Active' || rowData.sumpTank === true;

      reset({
        categoryName: rowData.categoryName,
        primary: primaryValue,
        sortOrder: rowData.sortOrder.toString(),
        sumpTank: sumpTankValue,
      });
      setShowAddCategoryModal(true);
    },
    [reset],
  );

  // Table columns
  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'id',
        headerName: t('table.columns.id'),
        sortable: true,
        width: 120,
        maxWidth: 120,
        // filter: true,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponent: CustomAgTextFilter,
      },
      {
        field: 'categoryName',
        headerName: t('table.columns.category'),
        width: 250,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'sortOrder',
        headerName: t('form.showOrder'),
        width: 150,
        maxWidth: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
      },
      {
        field: 'primary',
        headerName: t('form.primary'),
        width: 150,
        maxWidth: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        cellRenderer: (params: any) => <Chips label={params?.data?.primary} />,
      },
      {
        field: 'sumpTank',
        headerName: t('form.sumpTank'),
        width: 150,
        maxWidth: 150,
        filter: 'agSetColumnFilter',
        floatingFilter: true,
        floatingFilterComponent: CustomAgMultiSelectFilter,
        cellRenderer: (params: any) => <Chips label={params?.data?.sumpTank} />,
      },
      {
        field: 'actions',
        headerName: '',
        width: 50,
        maxWidth: 50,
        flex: 0,
        sortable: false,
        pinned: 'right',
        cellRenderer: (params: any) => (
          <CustomActionButton
            menuOptions={[
              {
                label: t('buttons.edit'),
                onClick: () => handleEditClick(params.data),
              },
              {
                label: t('buttons.delete'),
                onClick: () => onDeleteClick(params.data),
              },
            ]}
          />
        ),
      },
    ],
    [handleEditClick, onDeleteClick, t],
  );

  useEffect(() => {
    setTableColumns(canAddorEdit ? columns : columns.filter((col) => col.field !== 'actions'));
  }, [canAddorEdit, columns]);

  // Form submission handler
  const onSubmit = useCallback(
    async (values: CategoryFormValues) => {
      try {
        if (editingCategory) {
          const res: any = await updateLubricantCategory({
            id: editingCategory.id,
            payload: {
              categoryName: values.categoryName,
              primary: values.primary,
              sortOrder: Number(values.sortOrder) || 0,
              sumpTank: values.sumpTank,
            },
          }).unwrap();
          showSuccessToast(res?.message);
        } else {
          const res: any = await createLubricantCategory({
            categoryName: values.categoryName,
            primary: values.primary,
            sortOrder: Number(values.sortOrder) || 0,
            sumpTank: values.sumpTank,
          }).unwrap();
          showSuccessToast(res?.message);
        }

        refetch();
        setShowAddCategoryModal(false);
        setEditingCategory(null);
        reset({
          categoryName: '',
          primary: false,
          sortOrder: '',
          sumpTank: false,
        });
      } catch (error) {
        logger.error('Failed to submit category', error as Error);
      }
    },
    [createLubricantCategory, updateLubricantCategory, editingCategory, refetch, reset],
  );

  // Modal close handler
  const handleCloseModal = useCallback(() => {
    setShowAddCategoryModal(false);
    setEditingCategory(null);
    reset({
      categoryName: '',
      primary: false,
      sortOrder: '',
      sumpTank: false,
    });
  }, [reset]);

  return (
    <Stack gap={1} sx={{ height: '100%', minHeight: 0 }}>
      <PageHeader
        actions={actionBtns}
        options={breadcrumbOptions}
        isFilterActive={isFilterActive}
        resetFilters={() => setResetFiltersSignal(true)}
      />
      <TableCardContent>
        <ClientSideAgGrid
          rowData={lubricantCategoriesList ?? []}
          columnDefs={tableColumns as any}
          loading={isLoading}
          className="lubricant-categories-grid"
          onFiltersActiveChange={(active: boolean) => {
            setIsFilterActive(active);
            setResetFiltersSignal(false);
          }}
          resetFiltersSignal={resetFiltersSignal}
        />
      </TableCardContent>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          open={showDeleteConfirm}
          title={t('lubricants.deleteCategory')}
          description={t('common.deleteConfirmation')}
          isSubmitting={isDeleting}
          onClose={onCloseDeleteConfirm}
          onConfirm={handleConfirmDelete}
        />
      )}

      {showAddCategoryModal && (
        <EditLubricantCategoryModal
          onClose={handleCloseModal}
          onSubmit={handleSubmit(onSubmit)}
          control={control}
          errors={errors}
          isEditMode={!!editingCategory}
          isSubmitting={isCreating || isUpdating}
        />
      )}
    </Stack>
  );
};

export default LubricantCategoriesContainer;
